import { customAlphabet } from 'nanoid';
import {
  ActionParameter,
  BaseActionParameter,
  CustomActionParameter,
  PrimitiveActionParameter,
} from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import { ArrayType, Flow, PrimitiveType } from 'src/features/flows/types/flow.types';
import {
  FlowActionConfig,
  InputConfig,
  InputOption,
  Output,
  PrimitiveOutput,
} from 'src/features/flows/types/flowAction.types';

import jsonStringifyDeterministic from 'json-stringify-deterministic';
export const stringify = jsonStringifyDeterministic;

export type PathObject = {
  prefix: string;
  pathName: string;
  displayPathname?: string;
  type: PrimitiveType | ArrayType;
};

// formats nested props to "parent.child.subchild" strings
export function formatThreadProperties(json: Output[] | undefined) {
  if (!json) {
    return [];
  }

  let result: PathObject[] = [];

  function traverse(obj: Output[] | Output, parentPath: string, displayPathname: string) {
    if (Array.isArray(obj)) {
      obj.forEach((item) => {
        traverse(item, parentPath, displayPathname);
      });
    } else if (obj instanceof Object) {
      let currentPath = parentPath ? parentPath + '.' + obj.id : obj.id;
      let currentDisplayPathname = displayPathname ? displayPathname + '.' + obj.name : obj.name;

      // Skip if it's an array type with object subitems (complex type)
      // @ts-ignore
      if (obj.type === 'object[]') {
        return;
      }

      // Handle object type with items
      if (obj.items && obj.type === 'object') {
        return traverse(obj.items, currentPath, currentDisplayPathname);
      }
      let pathObject = {
        prefix: 'thread',
        pathName: currentPath,
        type: obj.type as PrimitiveType | ArrayType,
        displayPathname: currentDisplayPathname,
      };
      result.push(pathObject);
    }
  }

  traverse(json, '', '');

  return result;
}

export function unionById(flow1: Flow[], flow2: Flow[]) {
  const union: Flow[] = [];

  for (let i = 0; i < flow1.length; i++) {
    if (flow2.find((flow) => flow.id === flow1[i].id)) {
      union.push(flow1[i]);
    }
  }

  return union;
}

export function generateNanoId(length = 15) {
  const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', length);
  return nanoid();
}

export function generateHash(data: string, length?: number): string {
  // Browser-compatible deterministic hash (FNV-1a variant producing hex string)
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < data.length; i++) {
    const ch = data.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(16, '0');
  return length ? hash.substring(0, length) : hash;
}

export function stripBraces(text: string): string {
  if (!text) {
    return '';
  }

  // Define the pattern to match {{ at the start and }} at the end of the string
  const pattern: RegExp = /^\{\{(.*)\}\}$/;

  // Use RegExp.prototype.exec to find the pattern at the beginning and end of the string
  const match: RegExpExecArray | null = pattern.exec(text);

  // If there's a match, return the content inside the braces, otherwise return the original text
  if (match) {
    return match[1];
  } else {
    return text;
  }
}

export function cleanType(type: string): PrimitiveType {
  if (type.endsWith('[]')) {
    return type.slice(0, -2) as PrimitiveType;
  }
  return type as PrimitiveType;
}

export function areObjectsEqual(obj1: Record<string, any>, obj2: Record<string, any>, verbose = false): boolean {
  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if ((!obj1 && obj2) || (obj1 && !obj2)) {
    return false;
  }
  if (!obj1 && !obj2) {
    return true;
  }

  if (typeof obj1 !== 'object') {
    return stringify(obj1) === stringify(obj2);
  }

  let keys1 = Object.keys(obj1);
  let keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    const difference = keys1.filter((x) => !keys2.includes(x)).concat(keys2.filter((x) => !keys1.includes(x)));
    for (const key of difference) {
      // avoid false positive when one key is explicitly "undefined" and the other is not set on the object
      if (obj1[key] !== obj2[key]) {
        if (verbose) {
          console.log(
            `key length mismatch: ${keys1.length} =/= ${keys2.length} 
            \nreceived keys 1: "${keys1.sort().join(', ')}"
            \nreceived keys 2: "${keys2.sort().join(', ')}"
            \n${difference} is/are missing`
          );
        }
        return false;
      }
    }
  }

  for (const key of keys1) {
    // second check: only catch if they are not both undefined
    if (!keys2.includes(key) && obj1[key] !== obj2[key]) {
      if (verbose) {
        console.log(`key mismatch: ${key} not in ${keys2}`);
      }
      return false;
    }

    let value1 = obj1[key];
    let value2 = obj2[key];

    if (typeof value1 === 'object' && typeof value2 === 'object') {
      if (!areObjectsEqual(value1, value2, verbose)) {
        return false;
      }
    } else {
      if (value1 !== value2) {
        if (verbose) {
          console.log(`value for ${key} is not equal: ${value1} - ${value2}`);
        }
        return false;
      }
    }
  }

  return true;
}

type DeepComparable = any;
/**
 * Deeply compares two values for equality, handling both arrays and objects
 * @param array1 First value to compare (can be array or object)
 * @param array2 Second value to compare (can be array or object)
 * @returns {boolean} True if values are deeply equal, false otherwise
 */
export function deepEqual(array1: DeepComparable, array2: DeepComparable) {
  const sameType = Array.isArray(array1) === Array.isArray(array2);

  if (!sameType) {
    return false;
  }

  if (Array.isArray(array1) && Array.isArray(array2)) {
    if (array1.length !== array2.length) {
      return false;
    }
    for (let i = 0; i < array1.length; i++) {
      if (!areObjectsEqual(array1[i], array2[i])) {
        return false;
      }
    }
    return true;
  }

  return areObjectsEqual(array1, array2);
}

// Function to recursively find an input by ID
export function findInputById(inputs: InputConfig[] | undefined, targetId: string): InputConfig | null {
  if (!inputs) {
    return null;
  }

  for (const input of inputs) {
    if (input.id === targetId) {
      return input;
    }

    if (input.type === 'custom[]' && input.items) {
      const foundInItems = findInputById(input.items, targetId);
      if (foundInItems) {
        return foundInItems;
      }
    }
  }

  return null;
}

export function getParameterById(parameters: ActionParameter[], targetId: string): ActionParameter | null {
  for (const param of parameters) {
    if (param.type === 'custom[]') {
      const foundInNested = getParameterById((param as CustomActionParameter).values, targetId);
      if (foundInNested) {
        return foundInNested;
      }
    }

    if (param.id === targetId) {
      return param;
    }
  }
  return null;
}

// recursively finds the values of a parameter by its identifierKey
export function getParameterValuesByIdentifierKey<T extends ActionParameter['type']>(
  parameters: ActionParameter[],
  targetIdentifierKey: string
): (T extends 'custom[]' ? CustomActionParameter : PrimitiveActionParameter) | null {
  for (const param of parameters) {
    if (param.identifierKey === targetIdentifierKey) {
      return param as T extends 'custom[]' ? CustomActionParameter : PrimitiveActionParameter;
    }

    if (param.type === 'custom[]') {
      const nestedValues = param.values as ActionParameter[];
      const foundInNested = getParameterValuesByIdentifierKey(nestedValues, targetIdentifierKey);
      if (foundInNested) {
        return foundInNested as T extends 'custom[]' ? CustomActionParameter : PrimitiveActionParameter;
      }
    }
  }

  return null;
}

export function itemsToArray(item: PrimitiveOutput | Output[]) {
  let array: { type: string }[];
  if (!(item instanceof Array)) {
    array = [item];
  } else {
    array = [...item];
  }
  return array;
}

export function createEmptyActionParameterFromInputConfig(inputConfig: InputConfig): ActionParameter {
  const baseParameter: BaseActionParameter = {
    id: inputConfig.id,
    name: inputConfig.name,
    nodeBodyName: inputConfig.nodeBodyName,
    hasOptions: inputConfig.hasOptions,
    identifierKey: inputConfig.id + '_' + generateNanoId(),
  };

  if (inputConfig.type === 'custom[]') {
    return {
      ...baseParameter,
      type: 'custom[]',
      values: inputConfig.items?.map(createEmptyActionParameterFromInputConfig) || [],
    } as CustomActionParameter;
  } else {
    return {
      ...baseParameter,
      type: inputConfig.type as PrimitiveType | ArrayType,
      values: [],
      overrides: {},
    } as PrimitiveActionParameter;
  }
}

export function intersectionOfLists(...lists: InputOption[][]) {
  if (lists.length === 0) return [];

  // Start with the first list
  let intersection = new Map(lists[0].map((option) => [option.id, option]));

  // Iterate through the remaining lists
  for (let i = 1; i < lists.length; i++) {
    // Create a map from the current list
    const currentMap = new Map(lists[i].map((option) => [option.id, option]));

    // Keep only the elements that exist in both maps
    intersection = new Map([...intersection].filter(([id]) => currentMap.has(id)));

    // If intersection becomes empty, we can stop early
    if (intersection.size === 0) break;
  }

  // Convert the final Map back to an array and return
  return Array.from(intersection.values());
}

export function getActionConfig(
  flowActions: FlowActionConfig[],
  actionId: string,
  integrationType: string | undefined
) {
  return flowActions.find((action) => {
    const equalId = action.id === actionId;
    const defaultType = action.integrationType === 'default';
    // The action has no integrationType, so we don't need to be checking for it
    const noType = !action.integrationType || !integrationType;
    // The action has an integrationType, we should only match actions if the found action has the same integrationType
    const equalType = action.integrationType === integrationType;
    // The integrationType of the action is `demo`, this should always pass if we found the action by id
    const isDemoType = integrationType === 'demo';

    return equalId && (defaultType || noType || equalType || isDemoType);
  });
}

export type TypeMap = {
  string: string;
  number: number;
  boolean: boolean;
  date: Date;
  email: string;
  code: string;
  'string[]': string;
  'number[]': number;
  'boolean[]': boolean;
  'date[]': Date;
  'email[]': string;
};

type ElementTypeMap = {
  'string[]': 'string';
  'number[]': 'number';
  'boolean[]': 'boolean';
  'date[]': 'date';
  'email[]': 'email';
};

export function getElementType<T extends keyof ElementTypeMap>(arrayType: T): ElementTypeMap[T] {
  return arrayType.slice(0, -2) as ElementTypeMap[T];
}

export function convertToType<T extends keyof TypeMap>(type: T, value: string): TypeMap[T] {
  if (!value) return value as TypeMap[T];
  switch (type) {
    case 'boolean':
    case 'boolean[]':
      return (value === 'true') as TypeMap[T]; // this is the string value that is being set in the UI
    case 'number':
    case 'number[]':
      return Number(value) as TypeMap[T];
    case 'date':
    case 'date[]':
      return new Date(value) as TypeMap[T];
    default:
      return value as TypeMap[T];
  }
}

export function formatFacetPath(path: string[]) {
  // if the last part of the path is "label", we remove it
  if (path.length > 1 && path[path.length - 1] === 'label') {
    return path.slice(0, -1);
  }
  return path;
}

export function convertArrayToType(paramType: string, values: { id: string }[]): string[] | number[] | Date[] {
  if (paramType === 'string[]') {
    return values.map((value) => convertToType('string', value.id));
  } else if (paramType === 'number[]') {
    return values.map((value) => convertToType('number', value.id));
  } else if (paramType === 'date[]') {
    return values.map((value) => convertToType('date', value.id));
  } else if (paramType === 'email[]') {
    return values.map((value) => convertToType('email', value.id));
  } else {
    // Default to string array for other types
    return values.map((value) => convertToType('string', value.id));
  }
}

export function convertSingleToType(paramType: string, value: string | undefined): string | number | boolean {
  if (!value) return '';

  if (paramType === 'number') {
    return convertToType('number', value);
  } else if (paramType === 'boolean') {
    return convertToType('boolean', value);
  } else {
    // Return string for all other types (including date, email, code)
    return convertToType('string', value);
  }
}
